import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { getLocalizedString } from "@ankaadia/ankaadia-shared";
import { NewUiDashboardTalentPoolSharingData } from "@ankaadia/graphql";
import { EmptystateComponent } from "@ankaadia/ui";
import { TranslocoService } from "@jsverse/transloco";
import { derivedAsync } from "ngxtension/derived-async";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "primeng/tabs";
import { Skeleton } from "primeng/skeleton";
import { catchError, map, Observable, of } from "rxjs";
import { SettingsService } from "../../../shared/services/settings.service";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { NewUiDashboardCardHeaderComponent } from "../layout/new-ui-dashboard-card-header/new-ui-dashboard-card-header.component";
import { NewUiDashboardCardComponent } from "../layout/new-ui-dashboard-card/new-ui-dashboard-card.component";
import {
  DashboardListItem,
  NewUiDashboardListComponent,
} from "../layout/new-ui-dashboard-list/new-ui-dashboard-list.component";
import { NewUiDashboardService } from "../new-ui-dashboard.service";
import { TalentPoolConfigService } from "../../talent-pool-config/talent-pool-config.service";

@Component({
  selector: "new-ui-dashboard-talent-pool-sharings",
  templateUrl: "./new-ui-dashboard-talent-pool-sharings.component.html",
  imports: [
    TranslateDirective,
    NewUiDashboardCardComponent,
    NewUiDashboardCardHeaderComponent,
    NewUiDashboardListComponent,
    TabList,
    Tab,
    Tabs,
    TabPanels,
    TabPanel,
    EmptystateComponent,
    Skeleton,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewUiDashboardTalentPoolSharingsComponent {
  private readonly newUiDashboardService = inject(NewUiDashboardService);
  private readonly transloco = inject(TranslocoService);
  private readonly talentPoolConfigService = inject(TalentPoolConfigService);
  protected readonly settings = inject(SettingsService);

  protected readonly config = derivedAsync(() =>
    this.talentPoolConfigService.getParticipationInfo().pipe(catchError(() => of(null)))
  );

  protected get showOutgoingTab(): boolean {
    return this.settings.hasModule("talentPool") || this.config()?.isUserInPool;
  }

  protected get showIncomingTab(): boolean {
    const cfg = this.config();
    return this.settings.hasModule("talentPool") || cfg?.isContributorInPool || cfg?.canContributeToAnkaadiaPool;
  }

  protected readonly sharings = derivedAsync(() => this.getSharings().pipe(catchError(() => of(null))));

  private getSharings(): Observable<{
    myPool: DashboardListItem[];
    sharedToMe: DashboardListItem[];
  } | null> {
    return this.newUiDashboardService.getTalentPoolSharings().pipe(
      map((data) => ({
        myPool: data.myPoolSharings.map((s) => this.mapSharingToListItem(s)),
        sharedToMe: data.sharedToMe.map((s) => this.mapSharingToListItem(s)),
      }))
    );
  }

  private mapSharingToListItem(sharing: NewUiDashboardTalentPoolSharingData): DashboardListItem {
    const lang = this.transloco.getActiveLang();
    const poolName = getLocalizedString(sharing.talentPoolName, lang);
    return {
      rowIcon: "icon-[mi--share]",
      rowIconStyleClass: "text-info",
      name: poolName,
      groupIcon: "icon-[mi--high-rise-building]",
      group: sharing.organizationName,
      routeLink: "/app/talent-pools/view",
      queryParams: { poolId: sharing.talentPoolId },
    };
  }
}
